import { Alert, Card, Heading, Page, Pressable, Skeleton, Text } from '@ui/themed';
import { useEffect, useState } from 'react';
import { useEnv } from '../../../context/env-context';
import { useHandlers, withPageProviders } from '../../../context/page-context';
import type { ActionSummary, StaffActionCategory } from '../types';

type ManageActionsPageConfig = {
  category: StaffActionCategory;
  title: string;
  subtitle: (teamName: string) => string;
};

function ManageActionsPageInner({ category, title, subtitle }: ManageActionsPageConfig) {
  const env = useEnv();
  const { navigationHandler, apis } = useHandlers();
  const [loading, setLoading] = useState(true);
  const [allowedActions, setAllowedActions] = useState<ActionSummary[]>([]);
  const [unavailableActions, setUnavailableActions] = useState<ActionSummary[]>([]);

  useEffect(() => {
    async function load() {
      setLoading(true);
      const data = await apis.staff.actions(env.team.id, category);
      setAllowedActions(data.actions.allowed);
      setUnavailableActions(data.actions.unavailable);
      setLoading(false);
    }
    load();
  }, [apis.staff, env.team.id, category]);

  if (loading) return <Skeleton />;

  return (
    <Page title={title} description={subtitle(env.team.name)}>
      <Card>
        <div className="flex flex-col gap-8">
          {allowedActions.length === 0 && (
            <Alert variant="warning" headingLevel="h3">
              There are no actions available for this team.
            </Alert>
          )}
          <div>
            {allowedActions.map((action) => (
              <div key={action.type} className="border-s border-e border-t last:border-b">
                <Pressable
                  onClick={() =>
                    navigationHandler.navigate(`/staff/${category}/${action.type}`, {
                      excludeSiteEnv: true,
                    })
                  }
                  fullWidth
                >
                  <div className="w-full p-4 text-start">
                    <Heading level="h3" style="sm" weight="semibold">
                      {action.name}
                    </Heading>
                    <Text variant="secondary" size="sm">
                      {action.description}
                    </Text>
                  </div>
                </Pressable>
              </div>
            ))}
          </div>
          {unavailableActions.length > 0 && (
            <div>
              <div className="mb-2 flex flex-col gap-2">
                <Heading level="h3" style="md">
                  Unavailable Actions
                </Heading>
                <Text variant="secondary">
                  The following actions are not available at this time for this team
                </Text>
              </div>
              {unavailableActions.map((action) => (
                <div key={action.type} className="border-s border-e border-t last:border-b">
                  <div className="w-full bg-gray-100 p-4 text-start">
                    <Heading level="h3" style="sm" weight="semibold">
                      {action.name} —{' '}
                      <span className="text-orange-600 text-sm italic">{action.reason}</span>
                    </Heading>
                    <Text variant="secondary" size="sm">
                      {action.description}
                    </Text>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>
    </Page>
  );
}

export const ManageBillingPage = withPageProviders(() => (
  <ManageActionsPageInner
    category="manage-billing"
    title="Manage Billing"
    subtitle={(name) => `Manage the billing for ${name}`}
  />
));

export const ManageTeamPage = withPageProviders(() => (
  <ManageActionsPageInner
    category="manage-team"
    title="Manage Team"
    subtitle={(name) => `Manage team ${name}`}
  />
));
